// services/walletService.ts

import api from "./api";

export interface DepositData {
  amount: number;
  tx_id: string;
}

export interface WithdrawalData {
  amount: number;
  destination_address: string;
  memo?: string;
}

export interface InitiateWithdrawalData {
  amount: number;
  destination_address: string;
  memo?: string;
  timezone?: string; // IANA timezone string from browser, e.g. "Africa/Lagos"
}

export interface ConfirmWithdrawalData {
  otp: string;
}

export interface TransactionFilters {
  type?:
    | "deposit"
    | "withdrawal"
    | "trade_earning"
    | "airdrop"
    | "bot_earning"
    | "referral_earning"
    | "signup_bonus"
    | "signup_reward"
    | "bonus"
    | "bot_purchase"
    | "bot_capital_return"
    | "team_bonus";
  status?: "pending" | "completed" | "rejected";
  limit?: number;
  page?: number;
}

/**
 * Returned by GET /wallet/withdrawal-rules.
 *
 * daily_limit is this user's calculated limit based on their 3-tier
 * activity score (% of their last-7-day total earnings).
 *
 * tier        — which tier the user currently falls into: high | mid | low
 * week_earnings — their total earnings across all types in the last 7 days
 * pct_applied — the percentage that was applied to derive the limit
 * tier_label  — human-readable explanation of how the limit was calculated
 * floor_limit — the configured minimum floor the limit can never go below
 * tier_config — the current thresholds and percentages for all 3 tiers
 *               (useful for the "how is my limit calculated?" tooltip)
 */
export interface WithdrawalRules {
  allowed_days: number[]; // 0 = Sunday … 6 = Saturday
  daily_limit: number; // this user's calculated daily limit (XRP)
  today_dow: number; // server's current day-of-week
  is_allowed_today: boolean;
  withdrawn_today: number; // XRP already withdrawn today (pending + completed)
  remaining_today: number; // daily_limit − withdrawn_today
  // ── Tier context ──────────────────────────────────────────────────────────
  tier: "high" | "mid" | "low";
  week_earnings: number; // total earnings last 7 days (all types)
  pct_applied: number; // e.g. 80 (%)
  tier_label: string; // e.g. "High activity (80% of 620.00 XRP 7-day earnings)"
  floor_limit: number; // e.g. 20 XRP
  base_limit: number; // tier-based portion of the limit
  bonus_top_up: number; // extra XRP from level-up bonuses
  all_time_lvl_bonus: number; // total level-up bonus XRP earned ever
  total_withdrawn: number; // total XRP withdrawn (completed) ever
  available_bonus: number; // all_time_lvl_bonus - total_withdrawn (floor 0)
  level_bonus_pct: number; // % of available_bonus added as top-up
  tier_config: {
    tier1_threshold: number;
    tier1_pct: number;
    tier2_threshold: number;
    tier2_pct: number;
    tier3_pct: number;
    level_bonus_pct: number;
  };
  // Timezone context echoed back from the backend
  user_timezone: string; // e.g. "Africa/Lagos"
  user_local_time: string; // e.g. "2026-04-19 14:32:00" in that timezone
  user_local_date: string; // e.g. "2026-04-19"
}

export interface InitiateWithdrawalResponse {
  message: string;
  requires_otp: boolean;
  otp_expires_in: number;
}

const walletService = {
  getDepositAddress: async () => {
    const response = await api.get("/deposit-address");
    return response.data;
  },

  getBalance: async () => {
    const response = await api.get("/wallet/balance");
    return response.data;
  },

  deposit: async (depositData: DepositData) => {
    const response = await api.post("/wallet/deposit", depositData);
    return response.data;
  },

  initiateWithdrawal: async (
    withdrawalData: InitiateWithdrawalData,
  ): Promise<InitiateWithdrawalResponse> => {
    // Always inject the browser timezone so the backend can validate the
    // withdrawal day against the user's local midnight, not server UTC.
    const payload = {
      ...withdrawalData,
      timezone:
        withdrawalData.timezone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    const response = await api.post("/wallet/withdraw/initiate", payload);
    return response.data;
  },

  confirmWithdrawal: async (data: ConfirmWithdrawalData) => {
    const response = await api.post("/wallet/withdraw/confirm", data);
    return response.data;
  },

  resendWithdrawalOtp: async () => {
    const response = await api.post("/wallet/withdraw/resend-otp");
    return response.data;
  },

  cancelWithdrawal: async () => {
    const response = await api.post("/wallet/withdraw/cancel");
    return response.data;
  },

  // Legacy — kept for backward compatibility
  withdraw: async (withdrawalData: WithdrawalData) => {
    const response = await api.post("/wallet/withdraw", withdrawalData);
    return response.data;
  },

  getTransactions: async (filters?: TransactionFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.type) params.append("type", filters.type);
      if (filters.status) params.append("status", filters.status);
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.page) params.append("page", filters.page.toString());
    }
    if (!filters?.limit) params.append("limit", "20");
    const queryString = params.toString();
    const response = await api.get(
      queryString
        ? `/wallet/transactions?${queryString}`
        : `/wallet/transactions`,
    );
    return response.data;
  },

  /** Fetch withdrawal schedule + this user's activity-based limit and tier info.
   *  Passes the browser's local IANA timezone so day-of-week and midnight
   *  reset are calculated in the user's local time, not server UTC. */
  getWithdrawalRules: async (): Promise<WithdrawalRules> => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await api.get(
      `/wallet/withdrawal-rules?timezone=${encodeURIComponent(tz)}`,
    );
    return response.data;
  },

  getBotEarnings: async (filters?: TransactionFilters) => {
    const params = new URLSearchParams();
    if (filters?.type) params.append("type", filters.type);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const queryString = params.toString();
    const response = await api.get(
      queryString
        ? `/trading/bot-earnings?${queryString}`
        : `/trading/bot-earnings`,
    );
    return response.data;
  },

  getTradingStatus: async () => {
    const response = await api.get("/trading/status");
    return response.data;
  },

  getTradingHistory: async () => {
    const response = await api.get("/trading/history");
    return response.data;
  },

  getAirdrops: async () => {
    const response = await api.get("/airdrops");
    return response.data;
  },

  getMyAirdrops: async () => {
    const response = await api.get("/airdrops/my");
    return response.data;
  },
};

export default walletService;
